/**
 * FoundingBusinessPage — the single canonical Founding Business landing.
 *
 * Serves TWO states from the same route:
 *   1. Non-Founder → acquisition experience ($79.99 offer above benefits,
 *      live availability card, gold CTA that opens standalone Stripe
 *      Checkout for exactly $79.99).
 *   2. Founder     → status/membership dashboard (permanent Founding
 *      Business #NNN card, active lifetime benefits, Founding 100 Wall
 *      link — never the $79.99 sales pitch).
 *
 * The canonical position number is sourced from
 *   GET /api/founder/eligibility.founding_member_number
 * which in turn reads the `founder_positions` registry — the client never
 * invents a Founder number.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Crown, Sparkles, ShieldCheck, Trophy, Users, Percent, MessageSquare,
  Rocket, Star, Award, ArrowRight, CheckCircle2, Loader2, Lock,
  Calendar,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { API, useAuth } from "@/App";

const BENEFITS = [
  { icon: Percent,    text: "20% OFF eligible paid TBC Business Plans for life" },
  { icon: Sparkles,   text: "20% OFF eligible TBC-owned marketing & advertising services for life" },
  { icon: Calendar,   text: "Additional 30 days FREE on your first eligible paid Business Plan — 60 days free total" },
  { icon: Trophy,     text: "Exclusive Founder discounts on eligible TBC merchandise, services & partner offers" },
  { icon: Award,      text: "Permanent Founding Business badge on your profile" },
  { icon: Crown,      text: "Permanent recognition as one of TBC's first 100 businesses" },
  { icon: Star,       text: "Priority consideration for eligible TBC promotions & campaigns" },
  { icon: Users,      text: "Founder networking and promotional opportunities" },
  { icon: MessageSquare, text: "Direct input on new TBC features and platform improvements" },
];

export default function FoundingBusinessPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const canceled = params.get("canceled") === "1";

  const [eligibility, setEligibility] = useState(null);
  const [publicSummary, setPublicSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutBusy, setCheckoutBusy] = useState(false);

  const loadPublic = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/founder/positions/public/summary`);
      setPublicSummary(r.data);
    } catch {
      /* silent */
    }
  }, []);

  const loadEligibility = useCallback(async () => {
    if (!user) {
      setEligibility(null);
      return;
    }
    try {
      const r = await axios.get(`${API}/founder/eligibility`, { withCredentials: true });
      setEligibility(r.data);
    } catch {
      setEligibility(null);
    }
  }, [user]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadPublic(), loadEligibility()]).finally(() => setLoading(false));
  }, [loadPublic, loadEligibility]);

  useEffect(() => {
    if (canceled) toast.error("Checkout canceled. Your Founding position was released back to available.");
  }, [canceled]);

  const isFounder = !!eligibility?.is_founding_member && !!eligibility?.founding_member_number;
  const claimed = publicSummary?.claimed ?? eligibility?.total - (eligibility?.remaining ?? 0);
  const remaining = publicSummary?.remaining ?? eligibility?.remaining;
  const nextAvailable = publicSummary?.next_available ?? eligibility?.next_available_position;
  const soldOut = (remaining ?? 100) <= 0;

  const startCheckout = async () => {
    if (!user) {
      login();
      return;
    }
    if (!eligibility?.business_id) {
      toast.error("You need to list your business first before securing a Founding position.");
      navigate("/add-business");
      return;
    }
    setCheckoutBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/founder/checkout-standalone`,
        { business_id: eligibility.business_id, origin_url: window.location.origin },
        { withCredentials: true },
      );
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Couldn't start checkout. Please try again.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start checkout");
    } finally {
      setCheckoutBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-tbc-cream" data-testid="founding-business-page">
      <Navbar />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        {/* Page header — same for both states */}
        <div className="text-center">
          <p className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.24em] text-tbc-gold font-semibold">
            <Crown className="w-3.5 h-3.5" />
            Founding 100
          </p>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl text-tbc-navy leading-[1.02]" data-testid="founding-page-title">
            {isFounder ? (
              <>Your Founding <span className="text-tbc-gold italic">Business Status</span></>
            ) : (
              <>Become a <span className="text-tbc-gold italic">Founding Business</span></>
            )}
          </h1>
          <p className="mt-4 text-tbc-navy/75 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
            {isFounder
              ? "Thank you for being one of the first 100 businesses building TheBlkCommunity legacy."
              : "Secure one of only 100 permanent Founding Business positions and receive lifetime benefits."}
          </p>
        </div>

        {loading ? (
          <div className="mt-16 flex items-center justify-center text-tbc-navy/40 gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading…
          </div>
        ) : isFounder ? (
          <FounderStatusView
            eligibility={eligibility}
            publicSummary={publicSummary}
            navigate={navigate}
          />
        ) : (
          <NonFounderAcquisitionView
            claimed={claimed}
            remaining={remaining}
            nextAvailable={nextAvailable}
            soldOut={soldOut}
            checkoutBusy={checkoutBusy}
            hasBusiness={!!eligibility?.business_id}
            onCheckout={startCheckout}
            onSignIn={login}
            authed={!!user}
            priceDisplay={eligibility?.one_time_price_display || publicSummary?.one_time_price_display}
          />
        )}

        {/* Benefits — shared but rendered differently per state (labelled inside the components). */}
        <BenefitsSection isFounder={isFounder} />

        {/* Help card */}
        <div className="mt-12 rounded-2xl border border-tbc-navy/10 bg-white/70 backdrop-blur-sm p-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between max-w-3xl mx-auto">
          <div>
            <p className="text-xs uppercase tracking-widest text-tbc-navy/50 font-semibold">Help &amp; questions</p>
            <p className="text-sm text-tbc-navy/70 mt-0.5">Need help or have questions about Founding Business status?</p>
          </div>
          <Button
            variant="outline"
            className="rounded-full border-tbc-navy/20 text-tbc-navy hover:bg-tbc-navy hover:text-white"
            onClick={() => navigate("/contact")}
            data-testid="founding-contact-support"
          >
            Contact support
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Non-Founder state — premium acquisition experience
// ---------------------------------------------------------------------------
function NonFounderAcquisitionView({
  claimed, remaining, nextAvailable, soldOut, checkoutBusy, hasBusiness,
  onCheckout, onSignIn, authed, priceDisplay,
}) {
  // priceDisplay is `$79.99` (or whatever platform_settings says) with the
  // dollar sign already baked in. Split it so we can render the cents
  // portion in the gold accent.
  const priceMatch = /^\$?(\d+)([.,]\d{2})?$/.exec(priceDisplay || "$79.99");
  const dollars = priceMatch ? priceMatch[1] : "79";
  const cents = priceMatch ? (priceMatch[2] || ".99") : ".99";
  return (
    <div className="mt-10 grid lg:grid-cols-[1.15fr_1fr] gap-6 items-stretch">
      {/* Offer card — visually dominant */}
      <div className="relative rounded-3xl overflow-hidden bg-tbc-navy text-white p-8 sm:p-10 shadow-xl" data-testid="founding-offer-card">
        {/* Decorative gold ring */}
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-tbc-gold/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-24 w-72 h-72 rounded-full bg-tbc-gold/5 blur-3xl pointer-events-none" />

        <p className="relative inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-tbc-gold font-semibold">
          <Sparkles className="w-3.5 h-3.5" />
          One-time investment
        </p>
        <div className="relative mt-5 flex items-baseline gap-2">
          <span className="font-heading text-white text-7xl sm:text-8xl leading-none tracking-tight" data-testid="founding-price">
            ${dollars}
            <span className="text-tbc-gold">{cents}</span>
          </span>
        </div>
        <p className="relative text-[11px] uppercase tracking-[0.28em] text-white/60 mt-1">One-time payment · not a subscription</p>
        <p className="relative mt-5 text-white/85 text-sm leading-relaxed max-w-md">
          Join the first and only <strong className="text-tbc-gold">100 businesses</strong> permanently recognized as part of TheBlkCommunity&apos;s founding class.
        </p>

        {soldOut ? (
          <div className="relative mt-8 rounded-xl border border-white/20 bg-white/5 p-5 text-center" data-testid="founding-sold-out">
            <p className="font-heading text-2xl text-tbc-gold">The Founding 100 is complete</p>
            <p className="text-sm text-white/70 mt-1">All 100 positions have been claimed. Regular Business Plans remain available.</p>
            <Link to="/pricing" className="inline-block mt-4 text-xs text-white/80 underline underline-offset-4 hover:text-tbc-gold">
              View standard plans →
            </Link>
          </div>
        ) : (
          <>
            <Button
              onClick={authed ? onCheckout : onSignIn}
              disabled={checkoutBusy}
              className="relative mt-8 h-14 w-full rounded-full bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy font-heading text-lg shadow-lg disabled:opacity-70"
              data-testid="founding-cta-btn"
            >
              {checkoutBusy ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Starting checkout…</>
              ) : !authed ? (
                <>Sign in to Secure Your Spot <ArrowRight className="w-5 h-5 ml-1" /></>
              ) : (
                <>Secure My Founding Spot — {priceDisplay || "$79.99"} <ArrowRight className="w-5 h-5 ml-1" /></>
              )}
            </Button>
            {authed && !hasBusiness && (
              <p className="relative mt-3 text-center text-xs text-white/60">
                You&apos;ll need to <Link to="/add-business" className="text-tbc-gold underline">list your business</Link> first before checkout.
              </p>
            )}
            <div className="relative mt-4 flex items-center justify-center gap-4 text-[11px] text-white/60">
              <span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Secure Stripe Checkout</span>
              <span aria-hidden="true">·</span>
              <span>No recurring charges</span>
            </div>
          </>
        )}
      </div>

      {/* Availability card */}
      <div className="rounded-3xl bg-white border border-tbc-navy/10 p-8 flex flex-col justify-between shadow-sm" data-testid="founding-availability-card">
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-tbc-navy/50 font-semibold">Founding 100 status</p>
          <p className="mt-1 text-xs text-tbc-navy/60">Live availability from the permanent ledger.</p>

          <div className="mt-8 grid grid-cols-2 gap-6">
            <Stat
              value={claimed ?? "—"}
              suffix=" / 100"
              label="Claimed"
              tone="gold"
              testid="founding-stat-claimed"
            />
            <Stat
              value={remaining ?? "—"}
              suffix=""
              label="Positions remain"
              tone="navy"
              testid="founding-stat-remaining"
            />
          </div>
          <div className="mt-8 pt-6 border-t border-tbc-navy/10">
            <p className="text-[11px] uppercase tracking-[0.28em] text-tbc-navy/50 font-semibold">Next available spot</p>
            {soldOut ? (
              <>
                <p className="mt-1 font-heading text-4xl text-tbc-gold leading-tight" data-testid="founding-next-available">
                  All 100 claimed
                </p>
                <p className="mt-1 text-xs text-tbc-navy/60">The Founding class is complete. Regular Business Plans remain open.</p>
              </>
            ) : (
              <>
                <p className="mt-1 font-heading text-6xl text-tbc-navy leading-none" data-testid="founding-next-available">
                  {nextAvailable ? `#${String(nextAvailable).padStart(2, "0")}` : "—"}
                </p>
                <p className="mt-1 text-xs text-tbc-navy/60">The next business to secure their spot receives this permanent Founding Business number.</p>
              </>
            )}
          </div>
        </div>
        <Link
          to="/founding-100"
          className="mt-8 inline-flex items-center gap-1.5 text-xs text-tbc-navy/70 hover:text-tbc-navy font-medium"
          data-testid="founding-view-wall-link"
        >
          View the Founding 100 wall
          <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}

function Stat({ value, suffix, label, tone, testid }) {
  const toneCls =
    tone === "gold" ? "text-tbc-gold" : tone === "navy" ? "text-tbc-navy" : "text-tbc-navy";
  return (
    <div>
      <p className={`font-heading text-5xl leading-none ${toneCls}`} data-testid={testid}>
        {value}
        <span className="text-tbc-navy/40 text-base font-normal">{suffix}</span>
      </p>
      <p className="mt-2 text-[10px] uppercase tracking-[0.28em] text-tbc-navy/50">{label}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Founder state — permanent status experience
// ---------------------------------------------------------------------------
function FounderStatusView({ eligibility, publicSummary, navigate }) {
  const positionNum = eligibility?.founding_member_number;
  const joined = eligibility?.first_paid_plan_activated_at || null;
  const paddedNum = String(positionNum || "").padStart(3, "0");
  return (
    <div className="mt-10 grid lg:grid-cols-[1.15fr_1fr] gap-6 items-stretch">
      {/* Status card — permanent Founding Business ticket */}
      <div className="relative rounded-3xl overflow-hidden bg-tbc-navy text-white p-8 sm:p-10 shadow-xl" data-testid="founder-status-card">
        <div className="absolute -top-24 -right-16 w-64 h-64 rounded-full bg-tbc-gold/10 blur-3xl pointer-events-none" />

        <p className="relative inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-tbc-gold font-semibold">
          <Crown className="w-3.5 h-3.5" />
          Your status
        </p>
        <div className="relative mt-5">
          <p className="text-[11px] uppercase tracking-[0.28em] text-white/60">Founding Business</p>
          <p className="font-heading text-white text-7xl sm:text-8xl leading-none tracking-tight" data-testid="founder-status-number">
            #{paddedNum}
          </p>
          <p className="mt-2 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.28em] text-tbc-gold font-semibold">
            <ShieldCheck className="w-3.5 h-3.5" />
            Permanent
          </p>
        </div>
        <p className="relative mt-5 text-white/85 text-sm leading-relaxed max-w-md">
          Your Founding spot is permanent and comes with exclusive lifetime benefits. This position is reserved in the Founding 100 ledger and cannot be reassigned.
        </p>
        {joined && (
          <p className="relative mt-4 text-xs text-white/60">
            Joined: <span className="text-white/85">{new Date(joined).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</span>
          </p>
        )}
        <div className="relative mt-6 flex flex-col sm:flex-row gap-3">
          <Button
            onClick={() => navigate(`/founding-100#${paddedNum}`)}
            className="h-12 rounded-full bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy font-heading"
            data-testid="founder-view-wall-btn"
          >
            View Founding 100 wall
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
          <Button
            onClick={() => navigate("/dashboard")}
            variant="outline"
            className="h-12 rounded-full border-white/25 bg-transparent text-white hover:bg-white hover:text-tbc-navy"
            data-testid="founder-manage-dashboard-btn"
          >
            Manage business
          </Button>
        </div>
      </div>

      {/* Progress card — informational for existing founders */}
      <div className="rounded-3xl bg-white border border-tbc-navy/10 p-8 flex flex-col justify-between shadow-sm" data-testid="founder-progress-card">
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-tbc-navy/50 font-semibold">Founding 100 progress</p>
          <p className="mt-1 text-xs text-tbc-navy/60">Where the founding class stands right now.</p>

          <div className="mt-8 grid grid-cols-2 gap-6">
            <Stat
              value={publicSummary?.claimed ?? "—"}
              suffix=" / 100"
              label="Claimed"
              tone="gold"
              testid="founder-progress-claimed"
            />
            <Stat
              value={publicSummary?.remaining ?? "—"}
              suffix=""
              label="Positions remain"
              tone="navy"
              testid="founder-progress-remaining"
            />
          </div>
          {publicSummary?.next_available && (
            <div className="mt-8 pt-6 border-t border-tbc-navy/10">
              <p className="text-[11px] uppercase tracking-[0.28em] text-tbc-navy/50 font-semibold">Next available spot</p>
              <p className="mt-1 font-heading text-6xl text-tbc-navy leading-none">
                #{String(publicSummary.next_available).padStart(2, "0")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Benefits (shared)
// ---------------------------------------------------------------------------
function BenefitsSection({ isFounder }) {
  const label = isFounder ? "Your lifetime benefits" : "Founding Business benefits";
  return (
    <div className="mt-14" data-testid="founding-benefits-section">
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-tbc-gold font-semibold">🏆 {isFounder ? "Active" : "Perks"}</p>
          <h2 className="font-heading text-tbc-navy text-2xl sm:text-3xl mt-1">{label}</h2>
        </div>
        {isFounder && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] uppercase tracking-widest font-semibold" data-testid="founder-benefits-active-chip">
            <CheckCircle2 className="w-3 h-3" />
            Active
          </span>
        )}
      </div>
      <div className="rounded-2xl border border-tbc-navy/10 bg-tbc-navy text-white overflow-hidden">
        <ul className="divide-y divide-white/10">
          {BENEFITS.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-start gap-4 px-6 py-4">
              <span className="flex-none w-9 h-9 rounded-full bg-tbc-gold/15 border border-tbc-gold/30 flex items-center justify-center">
                {isFounder ? (
                  <CheckCircle2 className="w-4 h-4 text-tbc-gold" />
                ) : (
                  <Icon className="w-4 h-4 text-tbc-gold" />
                )}
              </span>
              <span className="text-sm text-white/90 leading-relaxed pt-1">{text}</span>
            </li>
          ))}
        </ul>
      </div>
      <p className="mt-3 text-[11px] text-tbc-navy/50 leading-relaxed">
        Founding Business perks are non-transferable and remain with the business as long as it stays active on TBC. Discount stacks are subject to TBC&apos;s eligibility rules — see the Terms of Service for details.
      </p>
    </div>
  );
}
